import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { LabValue, PageableModel } from '@mona/models';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { omit } from '@mona/shared/utils';
import { LabValuesState } from '../../entities';
import { LabValuesAction } from '../actions';

export const labValuesAdapter: EntityAdapter<LabValue> = createEntityAdapter<LabValue>({
    selectId: (item: LabValue) => item.id,
});

export const initialState: LabValuesState = labValuesAdapter.getInitialState({
    isLoading: false,
    startDate: null,
    pageableData: null,
});

export const labValuesReducer = createReducer(
    initialState,

    on(LabValuesAction.loadLabValues, state => ({
        ...state,
        isLoading: true,
    })),

    on(LabValuesAction.loadLabValuesSuccess, LabValuesAction.loadLabValuesFailed, state => ({
        ...state,
        isLoading: false,
    })),

    on(LabValuesAction.setLoadingAction, (state, { isLoading }) => ({
        ...state,
        isLoading,
    })),

    on(LabValuesAction.loadLabValuesSuccess, (state, action: PageableModel<LabValue>) => {
        const { results, ...pageableData } = omit(action, 'type') as PageableModel<LabValue>;

        return labValuesAdapter.upsertMany(results, {
            ...state,
            isLoading: false,
            pageableData,
            startDate: results?.at(-1)?.date,
        });
    }),

    on(LabValuesAction.upsertLabValues, (state, { data }) =>
        labValuesAdapter.upsertMany(data, {
            ...state,
            isLoading: false,
        }),
    ),

    on(LabValuesAction.removeLabValues, (state, { ids }) =>
        labValuesAdapter.removeMany(ids, { ...state, isLoading: false }),
    ),

    /**
     * Belt-and-suspenders against any future effect that forgets to emit a terminal
     * action: clearing the entities also resets `isLoading`, so a fresh encounter mount
     * cannot inherit a stuck spinner from the previous session. Refs: MD-9750.
     */
    on(LabValuesAction.clearLabValues, EncountersActions.clearEncounter, state =>
        labValuesAdapter.removeAll({ ...state, startDate: null, isLoading: false }),
    ),

    on(LabValuesAction.addChanges, (state, { toRemoveIds, toUpdateEntities }) => {
        const newState = labValuesAdapter.removeMany(toRemoveIds, state);

        return labValuesAdapter.upsertMany(toUpdateEntities, newState);
    }),
);
